import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { subscribeWithAuthRecovery } from '../services/realtimeResilience';
import { fetchProjectLocks } from '../services/projectLockService';
import { LOCK_HEARTBEAT_MS, ProjectLock, pickLockQueryIds } from '../services/projectLockLogic';

/**
 * Die geltenden Projektsperren fuer eine LISTE — damit man schon vor dem Klick
 * sieht, wer in einem Projekt sitzt, statt ueberrascht im Lesemodus zu landen.
 *
 * Der Hook holt Daten und haelt sie frisch, sonst nichts. Ob und was an einer
 * Zeile erscheint, entscheidet `describeProjectLockBadge` in
 * projectLockLogic.ts — dort ohne DOM testbar, hier nicht (kein jsdom).
 *
 * FAIL-OPEN in jeder Lage: `fetchProjectLocks` wirft nie, und ohne Antwort
 * bleibt die Map leer. Die Liste sieht dann exakt aus wie vor diesem Hinweis.
 */

/** Leere Map als Konstante — spart bei jedem Renderdurchlauf eine neue Instanz. */
const EMPTY_LOCKS: Map<string, ProjectLock> = new Map();

/**
 * Kanalnamen muessen eindeutig sein: zwei Abos mit demselben Topic teilen sich
 * bei supabase-js die Verbindung und stolpern beim Abbau uebereinander.
 */
let channelSeq = 0;

/**
 * Wartezeit, bevor eine geaenderte Projektmenge nachgeladen wird.
 *
 * Die Menge aendert sich bei jedem Tastendruck im Suchfeld und bei jedem
 * Filterklick. Ohne diese Bremse liefe je Buchstabe eine Abfrage.
 */
const IDS_SETTLE_MS = 400;

export interface ProjectLockView {
    /** Sperrzeilen je Projektkennung. Enthaelt auch abgelaufene — siehe unten. */
    locks: Map<string, ProjectLock>;
    /**
     * Der Zeitpunkt, gegen den die Anzeige rechnen soll.
     *
     * Bewusst nicht `Date.now()` in der Komponente: eine Sperre laeuft auch
     * dann ab, wenn sich an den Daten nichts aendert. Dieser Wert zieht mit
     * jedem Herzschlag nach und loest damit genau ein Neuzeichnen aus, das die
     * abgelaufene Sperre verschwinden laesst.
     */
    nowMs: number;
}

/**
 * @param projectIds Die gerade sichtbaren Projekte. Reihenfolge zaehlt: ueber
 *   `LOCK_LIST_MAX_PROJECTS` hinaus wird abgeschnitten.
 * @param enabled Aus, wenn die Liste gar nicht auf dem Schirm steht (z. B. ein
 *   geschlossener Auswahldialog). Dann laeuft weder Abo noch Zeitgeber.
 */
export const useProjectLocks = (
    projectIds: ReadonlyArray<string | null | undefined>,
    enabled: boolean = true,
): ProjectLockView => {
    // Aus der bei jedem Render neuen Array-Instanz eine stabile Abhaengigkeit
    // machen: sonst raeumte der Effekt unten Abo und Zeitgeber im Sekundentakt
    // ab und baute sie neu auf.
    const idKey = pickLockQueryIds(projectIds).join(',');
    const ids = useMemo(() => (idKey === '' ? [] : idKey.split(',')), [idKey]);

    const [locks, setLocks] = useState<Map<string, ProjectLock>>(EMPTY_LOCKS);
    const [nowMs, setNowMs] = useState<number>(() => Date.now());

    useEffect(() => {
        if (!enabled || ids.length === 0) {
            setLocks(EMPTY_LOCKS);
            return;
        }

        let cancelled = false;
        const visible = new Set(ids);

        const refreshAll = () => {
            void fetchProjectLocks(ids).then((map) => {
                if (cancelled) return;
                setLocks(map);
                setNowMs(Date.now());
            });
        };

        // Realtime meldet EINE Zeile — dann wird auch nur diese nachgezogen.
        // Ein voller Neuabruf je Herzschlag eines Kollegen waere bei mehreren
        // gleichzeitig geoeffneten Projekten Dauerlast ohne Mehrwert.
        const refreshOne = (projectId: string) => {
            if (!visible.has(projectId)) return;
            void fetchProjectLocks([projectId]).then((map) => {
                if (cancelled) return;
                const fresh = map.get(projectId) || null;
                setLocks((prev) => {
                    const next = new Map(prev);
                    // Kein Treffer heisst freigegeben — oder nicht lesbar. Beides
                    // fuehrt zu „keine Sperre bekannt", also Eintrag weg.
                    if (fresh) next.set(projectId, fresh);
                    else next.delete(projectId);
                    return next;
                });
                setNowMs(Date.now());
            });
        };

        // Erste Ladung verzoegert: bei Suche/Filter aendert sich `ids` sonst je
        // Tastendruck und schickt eine Abfrage hinterher.
        const settle = window.setTimeout(refreshAll, IDS_SETTLE_MS);

        // Ohne `project_id`-Filter: die Liste interessiert sich fuer viele
        // Projekte, und ein Kanal je Zeile waeren hunderte Abos. Gefiltert wird
        // beim Empfang ueber `visible`.
        const handle = subscribeWithAuthRecovery(supabase, () =>
            supabase
                .channel(`project-locks-list-${++channelSeq}`)
                .on(
                    'postgres_changes',
                    { event: '*', schema: 'public', table: 'project_locks' },
                    (payload: any) => {
                        // Bei DELETE steht die Kennung nur noch in `old`.
                        const id = payload?.new?.project_id || payload?.old?.project_id;
                        if (typeof id === 'string' && id !== '') refreshOne(id);
                    },
                ),
        );

        // Ausfallschutz und Ablaufuhr in einem. Realtime kann fehlen (der
        // Publikations-Block in project_locks.sql ist ausdruecklich optional),
        // und selbst bei stillstehenden Daten muss eine ablaufende Sperre aus
        // der Liste verschwinden.
        const timer = window.setInterval(() => {
            // Im Hintergrundtab wird nicht abgefragt — der Zeitstempel zieht
            // trotzdem nach, damit beim Zurueckkommen nichts Altes dasteht.
            if (typeof document !== 'undefined' && document.hidden) setNowMs(Date.now());
            else refreshAll();
        }, LOCK_HEARTBEAT_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(settle);
            window.clearInterval(timer);
            handle.close();
        };
    }, [enabled, ids]);

    return { locks, nowMs };
};
